import { useState } from 'react';
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  ConnectionIndicator,
  CopyButton,
  ControlledTabs,
  Input,
  Modal,
  ProviderLogo,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { usePlatformEvents } from '../../contexts/PlatformEventContext';
import { useIntegrationLiveEvents } from '../../hooks/useIntegrationLiveEvents';
import { useToast } from '../../components/ui/Toast';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import {
  AlertTriangle,
  KeyRound,
  Pause,
  Play,
  RotateCcw,
} from 'lucide-react';
import {
  useOrgIntegration,
  useOrgIntegrationEvents,
  useOrgIntegrationEventDetail,
  useOrgIntegrationRedeliver,
  useUpdateOrgIntegration,
  useDeleteOrgIntegration,
  useRoutingRules,
} from '../../hooks/useOrgIntegrations';
import { useProviders } from '../../hooks/useIntegrations';
import { formatRelativeTime } from '../activity/utils';
import IntegrationDeliveriesCard from '../integrations/IntegrationDeliveriesCard';
import QuickSetupPanel from '../integrations/QuickSetupPanel';
import SlackBotCredentialsPanel from '../integrations/SlackBotCredentialsPanel';
import { isSlackIntegration } from '../../lib/slack-integration';
import { isDynamicRule } from '../../types/integrations';
import RoutingRulesTab from './RoutingRulesTab';
import BackLink from '../../components/navigation/BackLink';

type TabId = 'overview' | 'rules' | 'events';

const TAB_IDS: TabId[] = ['overview', 'rules', 'events'];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TAB_IDS as string[]).includes(value);
}

export default function OrgIntegrationDetail() {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const { data: integration, isLoading, error } = useOrgIntegration(integrationId);
  const { data: providers } = useProviders();
  const { data: rules } = useRoutingRules(integrationId);
  const updateIntegration = useUpdateOrgIntegration();
  const deleteIntegration = useDeleteOrgIntegration();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'overview';
  const setActiveTab = (id: TabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'overview') {
          next.delete('tab');
        } else {
          next.set('tab', id);
        }
        return next;
      },
      { replace: true },
    );
  };
  const [deleteModal, setDeleteModal] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [updateSecretModal, setUpdateSecretModal] = useState(false);
  const [newSecretInput, setNewSecretInput] = useState('');

  // Page-level WS subscription so the Activity card refreshes on incoming
  // events regardless of which tab is open. The hook invalidates the
  // ['org','integrations',id] query prefix on each live event, which refetches
  // both the integration GET (Activity counters) and the events list.
  // `IntegrationDeliveriesCard` still has its own subscription for the
  // event-row buffer; we pass `hideConnectionIndicator` to keep the dot here
  // on the tab strip instead of duplicating it inside that card.
  useIntegrationLiveEvents(integrationId, ['org', 'integrations', integrationId]);
  const { isConnected, diagnostics } = usePlatformEvents();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !integration) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load integration</p>
        <Link
          to={routes.console.org.integrations}
          className="text-primary-600 hover:text-primary-500 mt-2 inline-block"
        >
          Back to integrations
        </Link>
      </div>
    );
  }

  const providerInfo = providers?.find((p) => p.name === integration.provider);
  const providerDisplayName =
    providerInfo?.displayName ??
    getProviderFallback(integration.provider).displayName;
  const requiresUserSecret = providerInfo?.secretMode === 'provider-issued';
  const secretLabel = providerInfo?.secretField?.label ?? 'Webhook secret';
  const secretHelp =
    providerInfo?.secretField?.helpText ??
    'Paste the new secret issued by the provider.';

  const handleDelete = async () => {
    try {
      await deleteIntegration.mutateAsync(integration.integrationId);
      success('Integration deleted');
      navigate(routes.console.org.integrations);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = integration.status === 'active' ? 'paused' : 'active';
    try {
      await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { status: newStatus },
      });
      success(`Integration ${newStatus === 'active' ? 'resumed' : 'paused'}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  // The auto-route DM rule's *presence* is the state — there is no separate
  // flag echoed back on the integration — so derive the toggle from the rules.
  const autoRouteEnabled = (rules ?? []).some(isDynamicRule);
  const handleToggleAutoRoute = async (checked: boolean) => {
    try {
      await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { autoRouteDmToPersonalSpace: checked },
      });
      success(
        checked
          ? 'DMs will route to each sender’s personal space'
          : 'Auto-routing of DMs disabled',
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  const handleRotateSecret = async () => {
    try {
      const updated = await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { rotateSecret: true },
      });
      setRotatedSecret(updated.webhookSecret ?? null);
      success('Webhook secret rotated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to rotate secret');
    }
  };

  const handleUpdateSecret = async () => {
    try {
      await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { webhookSecret: newSecretInput.trim() },
      });
      success('Signing secret updated');
      setUpdateSecretModal(false);
      setNewSecretInput('');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update secret');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'rules', label: `Routing rules (${rules?.length ?? 0})` },
    { id: 'events', label: 'Events' },
  ];

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.org.integrations}>Integrations</BackLink>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <ProviderLogo
            slug={integration.provider}
            displayName={providerDisplayName}
            className="h-12 w-12"
            fallbackClassName="text-sm"
          />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">
                {integration.displayName}
              </h1>
              <Badge variant={getStatusVariant(integration.status)}>
                {integration.status}
              </Badge>
              <Badge variant="gray">Organization</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {providerDisplayName} integration
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleToggleStatus}
            isLoading={updateIntegration.isPending}
          >
            {integration.status === 'active' ? (
              <>
                <Pause className="h-4 w-4 mr-2" /> Pause
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" /> Resume
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Rotated secret alert — Optr-generated only. Provider-issued secrets
          never echo a value back. */}
      {rotatedSecret && !requiresUserSecret && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              New webhook secret — save it now
            </p>
            <div className="flex items-center gap-3 mt-2">
              <code className="text-sm bg-white dark:bg-black/20 px-3 py-1.5 rounded-md font-mono break-all flex-1">
                {rotatedSecret}
              </code>
              <CopyButton value={rotatedSecret} size="md" />
            </div>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
              Update this secret in your provider&apos;s webhook settings. The
              old secret will no longer validate.
            </p>
            <button
              onClick={() => setRotatedSecret(null)}
              className="text-xs text-yellow-700 dark:text-yellow-300 underline mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <span className="absolute right-0 top-3 z-10">
          <ConnectionIndicator
            isConnected={isConnected}
            diagnostics={diagnostics}
          />
        </span>
        <ControlledTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as TabId)}
        >
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card>
              <CardHeader title="Activity" />
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Total events
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {integration.eventCount}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Routed
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {integration.routedEventCount ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1">
                    Unrouted
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-xs cursor-help text-muted-foreground/70">
                          (?)
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-xs">
                        Events the integration received that didn&rsquo;t match
                        any routing rule. This is informational — only your
                        rules decide what gets forwarded.
                      </TooltipContent>
                    </Tooltip>
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-foreground">
                    {integration.unroutedEventCount ?? 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Last event
                  </dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {integration.lastEventAt
                      ? formatRelativeTime(integration.lastEventAt)
                      : 'Never'}
                  </dd>
                </div>
              </dl>
            </Card>

            <Card>
              <CardHeader title="Configuration" />
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Provider</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {providerDisplayName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Ingestion type
                  </dt>
                  <dd className="mt-1 text-sm text-foreground">
                    {integration.ingestionType}
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-muted-foreground">
                    Webhook URL
                  </dt>
                  <dd className="mt-1 flex items-center gap-3">
                    <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
                      {integration.webhookUrl}
                    </code>
                    <CopyButton value={integration.webhookUrl} size="md" />
                  </dd>
                </div>
                {integration.eventFilters && integration.eventFilters.length > 0 && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-muted-foreground">
                      Event filters
                    </dt>
                    <dd className="mt-1 flex flex-wrap gap-2">
                      {integration.eventFilters.map((filter) => (
                        <Badge key={filter} variant="gray">
                          {filter}
                        </Badge>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>

            <QuickSetupPanel
              provider={integration.provider}
              providerDisplayName={providerDisplayName}
              documentationUrl={providerInfo?.documentationUrl}
              integration={integration}
            />

            {/* Slack bot tokens — one credential per workspace at org scope.
                This page is org-admin gated, so the viewer can manage them. */}
            {isSlackIntegration(integration) && (
              <SlackBotCredentialsPanel scope={{ kind: 'org' }} canManage />
            )}

            {/* Slack org-scoped only — seeds/removes the dynamic DM routing rule.
                The rule's presence is the state (see autoRouteEnabled). */}
            {isSlackIntegration(integration) && (
              <Card>
                <CardHeader title="Direct messages" />
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <label
                      htmlFor="auto-route-dm"
                      className="text-sm font-medium text-foreground"
                    >
                      Auto-route DMs to personal spaces
                    </label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Direct messages to the Operator app are delivered to each
                      sender&rsquo;s own personal space.
                    </p>
                  </div>
                  <Switch
                    id="auto-route-dm"
                    checked={autoRouteEnabled}
                    onCheckedChange={handleToggleAutoRoute}
                    disabled={updateIntegration.isPending}
                  />
                </div>
              </Card>
            )}

            <Card>
              <CardHeader
                title="Actions"
                description={
                  requiresUserSecret
                    ? 'Replace the stored signing secret if it has been rotated in the provider.'
                    : 'Rotate the webhook secret if it leaks. The current secret is invalidated immediately on rotation.'
                }
              />
              <div className="flex gap-3">
                {requiresUserSecret ? (
                  <Button
                    variant="secondary"
                    onClick={() => setUpdateSecretModal(true)}
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    Update signing secret
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={handleRotateSecret}
                    isLoading={updateIntegration.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Rotate webhook secret
                  </Button>
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'rules' && (
          <RoutingRulesTab
            integrationId={integration.integrationId}
            providerName={integration.provider}
          />
        )}

        {activeTab === 'events' && (
          <EventsTab
            integrationId={integration.integrationId}
            ruleHref={(ruleId) => `?tab=rules&selRule=${ruleId}`}
          />
        )}
        </ControlledTabs>
      </div>

      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete integration"
        message={`Delete "${integration.displayName}"? Routing rules attached to this integration will also be removed. Incoming webhooks will stop being processed. This cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteIntegration.isPending}
      />

      {/* Update signing secret modal (provider-issued only) */}
      <Modal
        isOpen={updateSecretModal}
        onClose={() => {
          setUpdateSecretModal(false);
          setNewSecretInput('');
        }}
        title="Update signing secret"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setUpdateSecretModal(false);
                setNewSecretInput('');
              }}
              disabled={updateIntegration.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSecret}
              isLoading={updateIntegration.isPending}
              disabled={!newSecretInput.trim()}
            >
              Update
            </Button>
          </>
        }
      >
        <Input
          label={secretLabel}
          type="password"
          value={newSecretInput}
          onChange={(e: { target: { value: string } }) =>
            setNewSecretInput(e.target.value)
          }
          placeholder="Paste new signing secret"
          hint={secretHelp}
        />
      </Modal>
    </div>
  );
}

/**
 * Org-wide deliveries view — same realtime WebSocket experience as the
 * space-scoped integration page, via the shared `IntegrationDeliveriesCard`.
 * The org events endpoint is integration-scoped (anchor space is authz only),
 * so this surfaces *all* of the integration's events, including ones that
 * matched no routing rule. Deep pagination / a `?routed=true|false` server
 * filter remains a known backend gap — historical is still capped at the
 * REST endpoint's last ~30 events (live WS buffer holds more).
 */
function EventsTab({
  integrationId,
  ruleHref,
}: {
  integrationId: string;
  ruleHref: (ruleId: string) => string;
}) {
  return (
    <IntegrationDeliveriesCard
      integrationId={integrationId}
      useEvents={useOrgIntegrationEvents}
      useEventDetail={useOrgIntegrationEventDetail}
      useRedeliver={useOrgIntegrationRedeliver}
      queryKeyPrefix={['org', 'integrations', integrationId]}
      ruleHref={ruleHref}
      hideConnectionIndicator
    />
  );
}
